import React, { useEffect, useRef, useState } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Dialog, DialogActions, Button } from '@mui/material';
import socketService from '../services/socketService';
import ProfileView from './profile/ProfileView';

// =============================================================================
// ProfileCard — a read-only "view someone's profile" dialog.
//
// Opened from a user row's "Profile" action. Renders the shared ProfileView (the
// same modern profile body used in the self view) in read-only mode: banner,
// large avatar, name, role/flag, "member since", and their bio.
//
// The profile bundle (bio, banner color, username color, role, avatar, country,
// member-since) is fetched from the server scoped to the requester's tenant via
// get-user-stats' `profile` field. The row we're opened with seeds the display
// (name/avatar/color) so the card paints instantly, then the server bundle fills
// in bio/banner/flag once it arrives. Stats are no longer shown here.
// =============================================================================

const useStyles = makeStyles()((theme) => ({
  paper: {
    backgroundColor: theme.palette.background.paper,
    minWidth: 380,
    maxWidth: 460,
    overflow: 'hidden', // let the banner reach the dialog edges
  },
}));

/**
 * @param open        bool
 * @param onClose     () => void
 * @param targetUser  { id, username, avatar, role, country, usernameColor, isGuest }
 */
const ProfileCard = ({ open, onClose, targetUser = {} }) => {
  const { classes } = useStyles();
  const [serverProfile, setServerProfile] = useState(null);

  const userId = targetUser?.id;

  useEffect(() => {
    if (!open || !userId) {
      setServerProfile(null);
      return;
    }
    let cancelled = false;
    socketService
      .getUserStats(userId)
      .then((res) => {
        if (!cancelled) setServerProfile(res?.profile || null);
      })
      .catch(() => {
        if (!cancelled) setServerProfile(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, userId]);

  // Merge: the row seeds identity so the card paints instantly; the server bundle
  // (bio/banner/flag/member-since) overrides once it arrives.
  const liveProfile = {
    username: targetUser?.username,
    usernameColor: targetUser?.usernameColor,
    role: targetUser?.isGuest ? 'guest' : targetUser?.role,
    avatarUrl: targetUser?.avatar,
    country: targetUser?.country,
    ...(serverProfile || {}),
  };

  // Closing sets targetUser to {} in the parent immediately, but the Dialog stays
  // mounted through its fade-out. Freeze the last profile so the closing animation
  // keeps showing the real name/avatar instead of flashing the "user" fallback.
  const shownRef = useRef(liveProfile);
  if (open && userId) shownRef.current = liveProfile;
  const profile = shownRef.current;

  return (
    <Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }}>
      <ProfileView profile={profile} editable={false} />
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProfileCard;
